import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog
} from "@angular/material/legacy-dialog";
import { Router } from '@angular/router';
import { CtWebapiGenericResponse } from '@ctsolution/ct-webapi';
import { WorkOrderPackagingConfiguration } from './work-order-packaging.configuration';
import { CTMGeneralService } from 'projects/ct-manager/src/app/_core/lib/general.service';
import { CtBinaryOperator, CtModelConfiguration, CTModelDatatableFilter, CtModelService } from '@ctsolution/ct-framework';
import { switchMap } from 'rxjs';
import { CTManagerItemStock } from 'projects/ct-manager/src/app/_core/classes/ct-manager-item-stock';
import { ItemHandling } from 'projects/ct-manager/src/app/_core/classes/item-handling';
import { SelectedItemsConfiguration } from '../../../handling/order-handling-wizard/order-handling-wizard-step2/selected-items/selected-items.configuration';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { WorkOrderPackagingFormComponent } from '../work-order-packaging-form/work-order-packaging-form.component';
import { WorkOrderPackagingCreateConfiguration } from '../work-order-packaging-form/work-order-packaging-form.configuration';
import { HandlingRouteData } from 'projects/ct-manager/src/app/_core/route-data/handling.route-data';
import { HandlingStateEnum } from 'projects/ct-manager/src/app/_core/enum/handling-state.enum';
import { CTManagerLocation } from 'projects/ct-manager/src/app/_core/classes/ct-manager-location';
import { SelectionModel } from '@angular/cdk/collections';
import { MatStepper } from '@angular/material/stepper';

/**
 * Componente per la gestione della creazione di packaging da Work Order.
 * Utilizza Material Design con stepper visivo per guidare l'utente attraverso:
 * 1. Selezione articoli da inserire nel packaging
 * 2. Definizione quantità per ogni articolo
 * 3. Selezione o creazione del packaging
 * 4. Riepilogo finale e conferma
 * 5. Messaggio di successo
 */
@Component({
  selector: 'app-work-order-packaging',
  templateUrl: './work-order-packaging.component.html',
  styleUrls: ['./work-order-packaging.component.scss']
})
export class WorkOrderPackagingComponent {

  // =========================================================================
  // VIEWCHILD REFERENCES
  // =========================================================================
  @ViewChild('stepper') stepper!: MatStepper;

  // =========================================================================
  // DATA & STATE
  // =========================================================================
  /** Lista completa degli articoli disponibili in stock */
  items: CTManagerItemStock[] | null = null;
  
  /** Lista paginata degli articoli da mostrare */
  paginatedItems: CTManagerItemStock[] = [];
  
  /** Indica se il componente è in fase di caricamento iniziale */
  isLoading: boolean = true;
  
  /** Configurazione per la gestione degli articoli selezionati */
  selectedItemConfiguration: SelectedItemsConfiguration = SelectedItemsConfiguration.create();
  
  /** Lista dei packaging disponibili per questo work order */
  packagies: any[] | null = [];
  
  /** OID della location parent (magazzino principale) */
  locationOid: number | null = null;

  // =========================================================================
  // PAGINATION
  // =========================================================================
  /** Numero di elementi per pagina */
  pageSize: number = 10;
  
  /** Pagina corrente (0-indexed) */
  currentPage: number = 0;

  // =========================================================================
  // MATERIAL TABLE
  // =========================================================================
  /** Colonne da visualizzare nella tabella Material */
  displayedColumns: string[] = ['select', 'code', 'name', 'quantity'];
  
  /** Selection model per gestire checkbox multiple selection */
  selection!: SelectionModel<CTManagerItemStock>;

  // =========================================================================
  // FORMS
  // =========================================================================
  /** Form per la gestione delle quantità degli articoli selezionati */
  itemForm: FormGroup;
  
  /** Form per la selezione del packaging */
  packagingForm: FormGroup;

  // =========================================================================
  // STEPPER STATE TRACKING
  // =========================================================================
  /** Step corrente (1-5) */
  currentStep: number = 1;
  
  /** Flag per indicare se step 1 è completato */
  step1Completed: boolean = false;
  
  /** Flag per indicare se step 2 è completato */
  step2Completed: boolean = false;
  
  /** Flag per indicare se step 3 è completato */
  step3Completed: boolean = false;
  
  /** Flag per indicare se step 4 è completato */
  step4Completed: boolean = false;
  
  /** Flag per indicare se step 5 è completato */
  step5Completed: boolean = false;

  // =========================================================================
  // CONSTRUCTOR
  // =========================================================================
  constructor(
    public dialogRef: MatDialogRef<WorkOrderPackagingComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WorkOrderPackagingConfiguration,
    private router: Router,
    private generalService: CTMGeneralService,
    private formBuilder: FormBuilder,
    private dialog: MatLegacyDialog,
    private modelService: CtModelService<any>,
    private cdr: ChangeDetectorRef,
  ) {
    // Inizializzazione SelectionModel per Material Table
    // IMPORTANTE: Deve essere inizializzato nel constructor PRIMA del template rendering
    this.selection = new SelectionModel<CTManagerItemStock>(true, []);

    // Inizializzazione form per le quantità degli articoli
    this.itemForm = this.formBuilder.group({
      itemsArray: this.formBuilder.array([])
    });

    // Inizializzazione form per la selezione del packaging
    this.packagingForm = this.formBuilder.group({
      packagingControl: new FormControl(null, Validators.required)
    });
  }

  // =========================================================================
  // LIFECYCLE HOOKS
  // =========================================================================
  ngOnInit(): void {
    this.loadItemsFromWorkOrder();
  }

  // =========================================================================
  // PAGINATION METHODS
  // =========================================================================
  /**
   * Aggiorna la lista paginata in base alla pagina corrente
   */
  private updatePaginatedItems(): void {
    if (!this.items) {
      this.paginatedItems = [];
      return;
    }
    
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedItems = this.items.slice(startIndex, endIndex);
  }

  /**
   * Calcola il numero totale di pagine
   */
  getPageCount(): number {
    if (!this.items || this.items.length === 0) return 1;
    return Math.ceil(this.items.length / this.pageSize);
  }

  /**
   * Ottiene il range di elementi visualizzati (es: "1-10 of 50")
   */
  getPageRange(): string {
    if (!this.items || this.items.length === 0) return '0 of 0';
    
    const startIndex = this.currentPage * this.pageSize + 1;
    const endIndex = Math.min((this.currentPage + 1) * this.pageSize, this.items.length);
    return `${startIndex}-${endIndex} of ${this.items.length}`;
  }

  /**
   * Gestisce il cambio di dimensione pagina
   */
  onPageSizeChange(): void {
    this.currentPage = 0; // Reset alla prima pagina
    this.updatePaginatedItems();
  }

  /**
   * Va alla prima pagina
   */
  firstPage(): void {
    this.currentPage = 0;
    this.updatePaginatedItems();
  }

  /**
   * Va alla pagina precedente
   */
  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updatePaginatedItems();
    }
  }

  /**
   * Va alla pagina successiva
   */
  nextPage(): void {
    if (this.currentPage < this.getPageCount() - 1) {
      this.currentPage++;
      this.updatePaginatedItems();
    }
  }

  /**
   * Va all'ultima pagina
   */
  lastPage(): void {
    this.currentPage = this.getPageCount() - 1;
    this.updatePaginatedItems();
  }

  // =========================================================================
  // DATA LOADING
  // =========================================================================
  /**
   * Carica gli articoli disponibili per il work order corrente.
   * Esegue due chiamate in sequenza:
   * 1. Recupera la location principale del work order
   * 2. Carica lo stock disponibile in quella location
   */
  private loadItemsFromWorkOrder(): void {
    if (!this.data.WorkOrder?.Oid) return;

    this.generalService.getList({
      controller: 'location',
      filters: [
        CTModelDatatableFilter.create()
          .setField('Type.MacroType')
          .setValue(1)
          .setOperatorType(CtBinaryOperator.Equal),
        CTModelDatatableFilter.create()
          .setField('WorkOrder.Oid')
          .setValue(this.data.WorkOrder.Oid)
          .setOperatorType(CtBinaryOperator.Equal),
      ]
    }).pipe(
      switchMap((response: any) => {
        // Salva l'OID della location principale
        this.locationOid = response.Result.DataSource[0].Oid;

        // Recupera lo stock disponibile nella location
        return this.generalService.getList({
          controller: 'locationstock',
          filters: [
            CTModelDatatableFilter.create()
              .setField('Location.Oid')
              .setValue(this.locationOid)
              .setOperatorType(CtBinaryOperator.Equal),
          ]
        });
      })
    ).subscribe((locationStockResponse: any) => {
      this.items = [...locationStockResponse.Result.DataSource];
      this.isLoading = false;
      this.updatePaginatedItems(); // Inizializza la paginazione
      this.cdr.detectChanges();
    });
  }

  /**
   * Carica i packaging disponibili per questo work order.
   * Se viene fornito un selectedOid, preseleziona quel packaging nel form.
   * 
   * @param selectedOid - OID del packaging da preselezionare (opzionale)
   */
  async updatePackage(selectedOid: number | null = null): Promise<void> {
    // Se c'è un packaging da preselezionare, imposta il valore nel form
    if (selectedOid) {
      this.packagingForm.controls['packagingControl'].setValue(selectedOid);
    }

    this.cdr.detectChanges();

    // Carica i packaging disponibili per questo work order (tipo MacroType = 2)
    await this.generalService.getList({
      controller: 'location',
      filters: [
        CTModelDatatableFilter.create()
          .setField('WorkOrder.Oid')
          .setValue(this.data.WorkOrder.Oid)
          .setOperatorType(CtBinaryOperator.Equal),
        CTModelDatatableFilter.create()
          .setField('Type.MacroType')
          .setValue(2)
          .setOperatorType(CtBinaryOperator.Equal),
      ]
    }).subscribe((response: any) => {
      this.packagies = [...response.Result.DataSource];
    });
  }

  // =========================================================================
  // MATERIAL TABLE SELECTION
  // =========================================================================
  /**
   * Verifica se tutte le righe della tabella sono selezionate
   * @returns true se tutte le righe sono selezionate
   */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.items?.length ?? 0;
    return numSelected === numRows;
  }

  /**
   * Toggle della selezione master: seleziona/deseleziona tutte le righe
   */
  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.items?.forEach(row => this.selection.select(row));
    }
  }

  // =========================================================================
  // NAVIGATION & DIALOG CONTROL
  // =========================================================================
  /**
   * Gestisce la chiusura del dialog o il ritorno allo step precedente.
   * 
   * @param step - Numero dello step corrente da cui si sta uscendo
   */
  onNoClick(step: number): void {
    switch (step) {
      case 1:
        // Chiudi dialog - il parent component gestirà il refresh se necessario
        this.dialogRef.close({ action: 'cancel' });
        break;

      case 2:
        // Torna allo step 1 - non serve aggiornare currentStep, lo fa stepper
        this.stepper.previous();
        break;

      case 3:
        // Torna allo step 2
        this.stepper.previous();
        break;

      case 4:
        // Torna allo step 3
        this.stepper.previous();
        break;

      case 5:
        // Chiudi dialog dopo il successo E ricarica la pagina
        this.dialogRef.close({ action: 'saved', refresh: true });
        // Ricarica la pagina per aggiornare i dati
        window.location.reload();
        break;

      default:
        break;
    }
  }

  /**
   * Gestisce l'avanzamento agli step successivi del wizard.
   * Valida i dati prima di procedere e prepara i dati per lo step successivo.
   * Chiama esplicitamente stepper.next() dopo aver completato le operazioni.
   * 
   * @param step - Numero dello step corrente
   */
  submit(step: number): void {
    switch (step) {
      case 1:
        // Step 1: Validazione selezione articoli
        if (this.selection.selected.length < 1) return;
        
        // Aggiorna la configurazione con gli articoli selezionati
        this.updateSelection(this.selectedItemConfiguration);
        
        // Popola il form delle quantità
        this.populateForm();
        
        // Marca come completato e avanza
        this.step1Completed = true;
        
        // IMPORTANTE: setTimeout per permettere al change detection di completare
        setTimeout(() => {
          this.stepper.next();
        }, 0);
        break;

      case 2:
        // Step 2: Validazione quantità
        if (!this.onSubmitStep2()) return;
        
        // Carica i packaging disponibili
        this.updatePackage();
        
        // Marca come completato e avanza
        this.step2Completed = true;
        
        // IMPORTANTE: setTimeout per permettere al change detection di completare
        setTimeout(() => {
          this.stepper.next();
        }, 0);
        break;

      case 3:
        // Step 3: Validazione selezione packaging
        if (this.packagingForm.invalid) return;
        
        // Marca come completato e avanza
        this.step3Completed = true;
        
        // IMPORTANTE: setTimeout per permettere al change detection di completare
        setTimeout(() => {
          this.stepper.next();
        }, 0);
        break;

      default:
        break;
    }
  }

  // =========================================================================
  // ITEM SELECTION & FORM MANAGEMENT
  // =========================================================================
  /**
   * Aggiorna la configurazione degli articoli selezionati con i dati
   * dalla selezione della Material Table.
   * 
   * @param selectedItemsConfiguration - Configurazione da aggiornare
   */
  private updateSelection(selectedItemsConfiguration: SelectedItemsConfiguration): void {
    // Reset della configurazione precedente
    selectedItemsConfiguration.resetItem();

    // Aggiunge gli articoli selezionati alla configurazione
    selectedItemsConfiguration.selectItems(this.selection.selected);
  }

  /**
   * Popola il FormArray con i controlli per l'inserimento delle quantità.
   * Crea un form control per ogni articolo selezionato con validazione.
   */
  private populateForm(): void {
    const itemsArray = this.itemForm.get('itemsArray') as FormArray;

    this.selectedItemConfiguration.itemHandlings.forEach(item => {
      // Verifica che l'elemento non sia già presente nel FormArray
      const alreadyExists = itemsArray.controls.some(
        control => control.get('code')?.value === item.Item?.Code
      );
      
      if (!alreadyExists) {
        // Crea un nuovo form group per questo articolo con validazione
        itemsArray.push(
          this.formBuilder.group({
            qty: [
              item.StartQty, 
              [
                Validators.required, 
                Validators.min(1), 
                Validators.max(item.CTManagerItemStock?.Stock!)
              ]
            ],
            code: [item.Item?.Code]
          })
        );
      }
    });
  }

  /**
   * Getter per ottenere il FormArray delle quantità
   * @returns FormArray contenente i controlli per le quantità
   */
  get itemsArray(): FormArray {
    return this.itemForm.get('itemsArray') as FormArray;
  }

  /**
   * Valida e salva le quantità inserite per ogni articolo.
   * 
   * @returns true se il form è valido e i dati sono stati salvati, false altrimenti
   */
  private onSubmitStep2(): boolean {
    if (this.itemForm.valid) {
      // Aggiorna le quantità nella configurazione in base ai valori del form
      this.selectedItemConfiguration.itemHandlings.forEach((item, index) => {
        item.StartQty = this.itemsArray.at(index).get('qty')?.value;
      });

      return true;
    } else {
      return false;
    }
  }

  // =========================================================================
  // PACKAGING OPERATIONS
  // =========================================================================
  /**
   * Apre il dialog per la creazione di un nuovo packaging.
   * Al termine della creazione, aggiorna la lista dei packaging disponibili
   * e preseleziona quello appena creato.
   */
  createPackaging(): void {
    const param: WorkOrderPackagingCreateConfiguration = 
      WorkOrderPackagingCreateConfiguration.create(this.locationOid, this.data.WorkOrder.Oid);
    
    // Resetta temporaneamente la lista per mostrare un loading
    this.packagies = null;
    
    const dialogRef = this.dialog.open(WorkOrderPackagingFormComponent, {
      data: param
    }).afterClosed().subscribe((res: number) => {
      // Dopo la chiusura, aggiorna la lista e preseleziona il nuovo packaging
      this.updatePackage(res);
      this.cdr.detectChanges();
    });
  }

  /**
   * Ottiene il nome del packaging attualmente selezionato nel form.
   * 
   * @returns Nome del packaging selezionato o null se non selezionato
   */
  getCurrentPackaging(): string | null {
    const packagingOid = this.packagingForm.get('packagingControl')?.value;
    
    if (packagingOid && this.packagies) {
      const selectedPack = this.packagies.find(pack => pack.Oid === packagingOid);
      return selectedPack ? selectedPack.Name : null;
    }

    return null;
  }

  // =========================================================================
  // FINAL SUBMISSION
  // =========================================================================
  /**
   * Crea il handling finale che sposta gli articoli dalla location principale
   * al packaging selezionato. Questo è il salvataggio finale del wizard.
   */
  createHandling(): void {
    const value: HandlingPackagingDTO = HandlingPackagingDTO.create();
    const conf: CtModelConfiguration<any> = CtModelConfiguration.create().setRouteData(HandlingRouteData());
    const packOid = this.packagingForm.get('packagingControl')?.value;

    // Prepara il DTO con tutti i dati necessari
    value
      .setHandlingState(HandlingStateEnum.Draft)
      .setLocationFrom(this.locationOid)
      .setLocationTo(packOid)
      .setItemHandlings(this.selectedItemConfiguration.itemHandlings);

    // Esegue il salvataggio
    this.modelService.putInfos(conf, value)?.subscribe({
      next: (response: CtWebapiGenericResponse<number | null>) => {
        // Marca step 4 come completato
        this.step4Completed = true;
        this.step5Completed = true;
        
        // Trigger change detection
        this.cdr.detectChanges();
        
        // Avanza allo step finale
        setTimeout(() => {
          this.stepper.next();
        }, 0);
      },
      error: (err) => {
        console.error('Errore durante la creazione del handling:', err);
        // TODO: Mostrare messaggio di errore all'utente con snackbar
      }
    });
  }
}

// ============================================================================
// DTO CLASS
// ============================================================================
/**
 * Data Transfer Object per la creazione di un handling di packaging.
 * Contiene tutte le informazioni necessarie per spostare articoli
 * da una location a un packaging.
 */
export class HandlingPackagingDTO {
  HandlingState: HandlingStateEnum | null = null;
  LocationFrom: CTManagerLocation | null = null;
  LocationTo: CTManagerLocation | null = null;
  ItemHandlings: ItemHandling[] | null = null;

  private constructor() { }

  /**
   * Factory method per creare un nuovo DTO
   * @returns Nuova istanza di HandlingPackagingDTO
   */
  public static create = (): HandlingPackagingDTO => new HandlingPackagingDTO();

  /**
   * Imposta la location di origine (FROM)
   * @param Oid - ID della location di origine
   * @returns this per method chaining
   */
  setLocationFrom(Oid: number | null): HandlingPackagingDTO {
    this.LocationFrom = { Oid } as CTManagerLocation;
    return this;
  }

  /**
   * Imposta la location di destinazione (TO)
   * @param Oid - ID della location di destinazione
   * @returns this per method chaining
   */
  setLocationTo(Oid: number | null): HandlingPackagingDTO {
    this.LocationTo = { Oid } as CTManagerLocation;
    return this;
  }

  /**
   * Imposta la lista degli item handling con le relative quantità.
   * Aggiorna anche lo stato di ogni item handling in base all'HandlingState generale.
   * 
   * @param value - Array di ItemHandling
   * @returns this per method chaining
   */
  setItemHandlings(value: ItemHandling[] | null): HandlingPackagingDTO {
    this.ItemHandlings = value;

    this.ItemHandlings?.forEach(itemHandling => {
      switch (this.HandlingState) {
        case HandlingStateEnum.Draft:
        case HandlingStateEnum.OnTheWay:
          itemHandling.setState(this.HandlingState);
          break;
        default:
          itemHandling.setState(
            itemHandling.StatusToggleComponentConfiguration?.control?.value ?? HandlingStateEnum.Draft
          );
          break;
      }
    });

    return this;
  }

  /**
   * Imposta lo stato generale dell'handling.
   * Se lo stato è Draft o OnTheWay, tutti gli item handling vengono impostati con lo stesso stato.
   * 
   * @param value - Stato dell'handling
   * @returns this per method chaining
   */
  setHandlingState(value: HandlingStateEnum | null): HandlingPackagingDTO {
    this.HandlingState = value;

    switch (value) {
      case HandlingStateEnum.Draft:
      case HandlingStateEnum.OnTheWay:
        this.ItemHandlings?.forEach(itemHandling => itemHandling.setState(value));
        break;
    }

    return this;
  }
}
